import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";

const MedicinePayment = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    medicine_company: null,
    amount: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [formErrors, setFormErrors] = useState({});
  const [companyData, setCompanyData] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const { data: companies, loading: loadingCompanies } = useFetchObjects(
    ["medicine-companies"],
    `medicine-companies/`,
    token,
  );

  const {
    handleAdd,
    resetForm,
    loading: saving,
  } = useAdd(
    "purchase-payments",
    token,
    t("finance.paymentSuccess"),
    t("finance.paymentFailed"),
  );

  useEffect(() => {
    if (companies) {
      setCompanyData(
        companies.map((company) => ({
          id: company.id,
          label: company.name,
          total_paid: company.total_paid,
          remaining_balance: company.remaining_balance,
        })),
      );
    }
  }, [companies]);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        medicine_company: null,
        amount: "",
        date: new Date().toISOString().split("T")[0],
      });
      setFormErrors({});
      setSelectedCompany(null);
    }
  }, [resetForm]);

  const handleSave = (e) => {
    e.preventDefault();
    const errors = {};

    if (!formData.medicine_company) {
      errors.medicine_company = t("finance.companyRequired");
    }
    if (!formData.amount || formData.amount <= 0) {
      errors.amount = t("finance.amountRequired");
    } else if (
      selectedCompany &&
      parseFloat(formData.amount) >
        parseFloat(selectedCompany.remaining_balance)
    ) {
      errors.amount = t("finance.amountExceedsBalanceDue");
    }
    if (!formData.date) {
      errors.date = t("finance.dateRequired");
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
    } else {
      handleAdd(formData);
    }
  };

  const handleCompanySelect = (value) => {
    setFormData({ ...formData, medicine_company: value?.id || null });
    setSelectedCompany(value);
    setFormErrors({ ...formErrors, medicine_company: "" });
  };

  return (
    <Fragment>
      <Box
        sx={{
          background: "linear-gradient(to bottom, #f9fafa, #ffffff)",
          py: 4,
          minHeight: "60vh",
          width: "100%",
        }}
      >
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("finance.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("finance.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box mb={6} textAlign="center">
          <Typography
            variant="h3"
            fontWeight={700}
            color="primary"
            gutterBottom
            sx={{ fontSize: isSmallScreen ? "1.8rem" : "2.5rem" }}
          >
            {t("finance.medicineCompanyPayment")}
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ fontSize: isSmallScreen ? "0.9rem" : "1rem" }}
          >
            {t("finance.paymentSubtitle")}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: 3,
                boxShadow: 3,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={600}
                color="primary"
                mb={2}
                sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
              >
                {t("finance.paymentForm")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <form onSubmit={handleSave}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    {loadingCompanies ? (
                      <CircularProgress />
                    ) : (
                      <Autocomplete
                        options={companyData}
                        getOptionLabel={(option) => option.label}
                        onChange={(e, value) => handleCompanySelect(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("finance.selectCompany")}
                            variant="outlined"
                            error={!!formErrors.medicine_company}
                            helperText={formErrors.medicine_company}
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("finance.amountPaid")}
                      variant="outlined"
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      error={!!formErrors.amount}
                      helperText={formErrors.amount}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("finance.paymentDate")}
                      variant="outlined"
                      fullWidth
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                    />
                  </Grid>
                </Grid>

                <Box mt={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={saving}
                    sx={{
                      fontSize: isSmallScreen ? "0.9rem" : "1rem",
                      fontWeight: 600,
                    }}
                  >
                    {saving ? (
                      <CircularProgress size={20} />
                    ) : (
                      t("finance.save")
                    )}
                  </Button>
                </Box>
              </form>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 3,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  fontWeight={600}
                  color="primary"
                  mb={2}
                  sx={{ fontSize: isSmallScreen ? "1.2rem" : "1.5rem" }}
                >
                  {t("finance.companyDetails")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {selectedCompany ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.companyName")}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedCompany.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.totalPaid")}
                      </Typography>
                      <Typography variant="h6">
                        {selectedCompany.total_paid} ؋
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {t("finance.remainingBalance")}
                      </Typography>
                      <Typography variant="h6" color="error">
                        {selectedCompany.remaining_balance} ؋
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography textAlign="center" color="textSecondary">
                    {t("finance.noCompanySelected")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default MedicinePayment;
